import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty, Radio, Tabs } from "antd";
import type { RadioChangeEvent } from "antd";
import PieChart from "../../components/PieChart/PieChart";
import ColumnChart from "../../components/ColumnChart/ColumnChart";
import "./styles.css";
import {
  getClientTypeIncome,
  getIncomeChartData,
  getIncomeSummary,
  getPaymentChartData,
  getTopEarningAccount,
  getTopEarningPlatform,
} from "../../redux/actions/dashboardActions";
import { RootState } from "../../redux/rootReducer";
import AvatarView from "../../components/_shared/AvatarView";
import clapUtils from "../../utils/clapUtils";
import IncomeCard from "../../components/Dashboard/IncomeCard";
import { AccountType, BarChartPeriod, Currency } from "../../shared/constant";
import SpinnerView from "../../components/_shared/SpinnerView";

const { TabPane } = Tabs;

function Dashboard() {
  const dispatch = useDispatch();
  const [period, setPeriod] = useState(BarChartPeriod.YEAR);
  const [ctiCurrency, setCTICurrency] = useState(Currency.BDT);
  const [tEACurrency, setTEACurrency] = useState(Currency.BDT);
  const [tEPCurrency, setTEPCurrency] = useState(Currency.BDT);
  const {
    incomeSummary,
    clientTypeincome,
    incomeChartData,
    paymentChartData,
    topClients,
    topPlatforms,
  } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(getIncomeSummary());
    dispatch(getTopEarningAccount({ currency: Currency.BDT }));
    dispatch(getTopEarningPlatform({ currency: Currency.BDT }));
    dispatch(getClientTypeIncome({ currency: Currency.BDT }));
  }, []);

  useEffect(() => {
    dispatch(getIncomeChartData({ period }));
    dispatch(getPaymentChartData({ period }));
  }, [period]);

  const onCTICurrencyChange = ({ target: { value } }: RadioChangeEvent) => {
    setCTICurrency(value);
    dispatch(getClientTypeIncome({ currency: value }));
  };
  const onTEACurrencyChange = ({ target: { value } }: RadioChangeEvent) => {
    dispatch(getTopEarningAccount({ currency: value }));
    setTEACurrency(value);
  };
  const onTEPCurrencyChange = ({ target: { value } }: RadioChangeEvent) => {
    dispatch(getTopEarningPlatform({ currency: value }));
    setTEPCurrency(value);
  };

  const handleFilterChange = ({ target: { value } }: RadioChangeEvent) => {
    setPeriod(value);
  };

  // FIXME rerender issue
  const operations = (
    <div>
      <Radio.Group
        defaultValue={BarChartPeriod.YEAR}
        onChange={handleFilterChange}
      >
        <Radio.Button value={BarChartPeriod.MONTH}>Last 30 days</Radio.Button>
        <Radio.Button value={BarChartPeriod.YEAR}>Last 12 months</Radio.Button>
      </Radio.Group>
    </div>
  );

  return (
    <div>
      <div>
        <div className="grid grid-cols-2 gap-x-6 pb-5 min-h-[289px]">
          <SpinnerView isFetching={!incomeSummary}>
            <div className="grid grid-cols-2 gap-3">
              {incomeSummary &&
                Object.keys(incomeSummary).map((item, index) => (
                  <IncomeCard
                    key={incomeSummary[item].title}
                    {...incomeSummary[item]}
                    index={index}
                  />
                ))}
            </div>
          </SpinnerView>
          <div className="default-border">
            <div className="flex justify-between px-6 py-3 flash-border">
              <div className="text-subheading16">
                Client Type wise Incomes Last 30 days
              </div>
              <div>
                <Radio.Group
                  defaultValue={Currency.BDT}
                  onChange={onCTICurrencyChange}
                  size="small"
                >
                  <Radio.Button value={Currency.BDT}>
                    {Currency.BDT}
                  </Radio.Button>
                  <Radio.Button value={Currency.EUR}>
                    {Currency.EUR}
                  </Radio.Button>
                  <Radio.Button value={Currency.USD}>
                    {Currency.USD}
                  </Radio.Button>
                </Radio.Group>
              </div>
            </div>
            <div>
              <div className="absolute px-6 py-3">
                {clapUtils.getCurrencyIcon(ctiCurrency)}{" "}
                {clientTypeincome?.total_income}
              </div>
              <SpinnerView isFetching={!clientTypeincome}>
                {clientTypeincome && (
                  <PieChart
                    data={clientTypeincome.distribution}
                    angleField="value"
                    colorField="type"
                  />
                )}
              </SpinnerView>
            </div>
          </div>
        </div>
        <div className="px-6 pt-3 pb-6 default-border min-h-[464px]">
          <Tabs defaultActiveKey="1" tabBarExtraContent={operations}>
            <TabPane tab="Income" key="1">
              <div>
                <SpinnerView isFetching={!incomeChartData}>
                  {incomeChartData && (
                    <ColumnChart
                      data={incomeChartData}
                      xField="date"
                      yField="value"
                      seriesField="name"
                    />
                  )}
                </SpinnerView>
              </div>
            </TabPane>
            <TabPane tab="Payments" key="2">
              <SpinnerView isFetching={!paymentChartData}>
                {paymentChartData && (
                  <ColumnChart
                    data={paymentChartData}
                    xField="date"
                    yField="value"
                    seriesField="name"
                  />
                )}
              </SpinnerView>
            </TabPane>
          </Tabs>
        </div>
        {/* TODO reuse two col  */}
        <div className="grid grid-cols-2 gap-6 my-5 min-h-[250px]">
          <div className="default-border">
            <div className="flex justify-between items-center px-6 py-4 flash-border">
              <div className="text-subheading16">
                Top Earning Clients Last 30 days
              </div>
              <div>
                <Radio.Group
                  defaultValue={Currency.BDT}
                  onChange={onTEACurrencyChange}
                >
                  <Radio.Button value={Currency.BDT}>
                    {Currency.BDT}
                  </Radio.Button>
                  <Radio.Button value={Currency.EUR}>
                    {Currency.EUR}
                  </Radio.Button>
                  <Radio.Button value={Currency.USD}>
                    {Currency.USD}
                  </Radio.Button>
                </Radio.Group>
              </div>
            </div>
            <SpinnerView isFetching={!topClients}>
              {topClients?.length ? (
                <div className="px-6">
                  <div className="grid grid-cols-10 py-3 flash-border">
                    <div className="col-span-3 text-subheading16">Client</div>
                    <div className="col-span-2 text-subheading16">
                      Client Type
                    </div>
                    <div className="col-span-3 text-subheading16">
                      Income Share
                    </div>
                    <div className="col-span-2 place-self-end text-subheading16">
                      Earnings
                    </div>
                  </div>
                  {topClients?.map((item: any) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-10 items-center py-3 flash-border"
                    >
                      <div className="col-span-3 flex items-center gap-2">
                        <AvatarView photo={item.client_logo} />
                        <div>{item.name}</div>
                      </div>
                      <div className="col-span-2">
                        {clapUtils.getClientTypes(item.client_types)}
                      </div>
                      <div className="col-span-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.keys(item.income_share).map((itm: any) =>
                            itm === AccountType.LABEL ? (
                              <div className="bg-flash-white rounded-full px-2">{`Producer (${item.income_share[itm]}%)`}</div>
                            ) : (
                              Object.keys(item.income_share[itm]).map(
                                (el) =>
                                  item.income_share[itm][el] && (
                                    <div className="bg-flash-white rounded-full px-2">
                                      {el === AccountType.LABEL
                                        ? "Producer"
                                        : el}{" "}
                                      ({item.income_share[itm][el]}%)
                                    </div>
                                  ),
                              )
                            ),
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 place-self-end">
                        {clapUtils.getCurrencyIcon(tEACurrency)} {item.income}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </SpinnerView>
          </div>
          <div className="default-border">
            <div className="flex justify-between items-center px-6 py-4 flash-border">
              <div className="text-subheading16">
                Top Earning Platforms Last 30 days
              </div>
              <div>
                <Radio.Group
                  defaultValue={Currency.BDT}
                  onChange={onTEPCurrencyChange}
                >
                  <Radio.Button value={Currency.BDT}>
                    {Currency.BDT}
                  </Radio.Button>
                  <Radio.Button value={Currency.EUR}>
                    {Currency.EUR}
                  </Radio.Button>
                  <Radio.Button value={Currency.USD}>
                    {Currency.USD}
                  </Radio.Button>
                </Radio.Group>
              </div>
            </div>
            <SpinnerView isFetching={!topPlatforms}>
              {topPlatforms?.length ? (
                <div className="px-6">
                  <div className="flex justify-between py-3 flash-border">
                    <div className="text-subheading16">Platform</div>
                    <div className="text-subheading16">Earnings</div>
                  </div>
                  {topPlatforms?.map((item: any) => (
                    <div
                      key={item.platform}
                      className="flex justify-between py-3 flash-border"
                    >
                      <div>{item.platform}</div>
                      <div>
                        {`${clapUtils.getCurrencyIcon(tEPCurrency)} ${
                          item.earnings
                        }`}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </SpinnerView>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
